import { useCallback, useEffect, useRef, useState } from "react";

export function useWoundInfinitePagination({
  hasMore,
  limit,
  onFetch,
  onScopeChange,
  scopeKey,
  initialPageLoaded = false,
  invalidationKey = 0,
}) {
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [hasLoadedInitialPage, setHasLoadedInitialPage] = useState(initialPageLoaded);

  const loadMoreRef = useRef(null);
  const observerRef = useRef(null);
  const onFetchRef = useRef(onFetch);
  const onScopeChangeRef = useRef(onScopeChange);
  const scopeKeyRef = useRef(null);
  const requestedPagesRef = useRef(new Set());
  const isFetchingRef = useRef(false);
  const isResettingRef = useRef(false);
  const isPageAdvancePendingRef = useRef(false);
  const requestGenerationRef = useRef(0);
  const initialPageLoadedRef = useRef(initialPageLoaded);
  const invalidationKeyRef = useRef(invalidationKey);

  useEffect(() => {
    onFetchRef.current = onFetch;
  }, [onFetch]);

  useEffect(() => {
    onScopeChangeRef.current = onScopeChange;
  }, [onScopeChange]);

  const fetchNextPage = useCallback(() => {
    if (!scopeKey) return;
    if (offset === 0 && initialPageLoadedRef.current) {
      setHasLoadedInitialPage(true);
      return;
    }

    const requestKey = `${scopeKey}:${offset}`;

    if (isFetchingRef.current) return;
    if (isResettingRef.current && offset !== 0) return;
    if (hasMore === false && offset !== 0) return;
    if (requestedPagesRef.current.has(requestKey)) return;

    const requestGeneration = requestGenerationRef.current;

    requestedPagesRef.current.add(requestKey);
    isPageAdvancePendingRef.current = false;
    isFetchingRef.current = true;
    setLoading(true);

    Promise.resolve()
      .then(() =>
        onFetchRef.current({
          patientId: scopeKey,
          generation: requestGeneration,
          offset,
        })
      )
      .finally(() => {
      if (requestGeneration !== requestGenerationRef.current) return;

      isFetchingRef.current = false;
      setLoading(false);
      if (offset === 0) {
        isResettingRef.current = false;
        setHasLoadedInitialPage(true);
      }
      });
  }, [hasMore, offset, scopeKey]);

  useEffect(() => {
    if (scopeKeyRef.current === scopeKey) return;

    scopeKeyRef.current = scopeKey;
    initialPageLoadedRef.current = initialPageLoaded;
    requestedPagesRef.current.clear();
    requestGenerationRef.current += 1;
    isFetchingRef.current = false;
    isResettingRef.current = true;
    isPageAdvancePendingRef.current = false;
    setHasLoadedInitialPage(false);
    setOffset(0);
    setLoading(false);
    invalidationKeyRef.current = invalidationKey;
    if (scopeKey) {
      onScopeChangeRef.current?.({
        patientId: scopeKey,
        generation: requestGenerationRef.current,
      });
    }
  }, [initialPageLoaded, invalidationKey, scopeKey]);

  useEffect(() => {
    fetchNextPage();
  }, [fetchNextPage, refreshKey]);

  const resetPagination = useCallback(() => {
    requestedPagesRef.current.clear();
    requestGenerationRef.current += 1;
    initialPageLoadedRef.current = false;
    isFetchingRef.current = false;
    isResettingRef.current = true;
    isPageAdvancePendingRef.current = false;
    setHasLoadedInitialPage(false);
    setRefreshKey(prev => prev + 1);
    setOffset(0);
    if (scopeKeyRef.current) {
      onScopeChangeRef.current?.({
        patientId: scopeKeyRef.current,
        generation: requestGenerationRef.current,
      });
    }
  }, []);

  useEffect(() => {
    if (invalidationKeyRef.current === invalidationKey) return;

    invalidationKeyRef.current = invalidationKey;
    resetPagination();
  }, [invalidationKey, resetPagination]);

  useEffect(() => {
    if (!scopeKey || !loadMoreRef.current) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          !isFetchingRef.current &&
          !isResettingRef.current &&
          !isPageAdvancePendingRef.current &&
          hasMore === true
        ) {
          isPageAdvancePendingRef.current = true;
          setLoading(true);
          setOffset((prev) => prev + limit);
        }
      },
      { root: null, rootMargin: "300px", threshold: 0 }
    );

    observerRef.current.observe(loadMoreRef.current);

    return () => observerRef.current?.disconnect();
  }, [hasMore, limit, scopeKey]);

  return {
    loading,
    hasLoadedInitialPage,
    loadMoreRef,
    resetPagination,
  };
}
